import React from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER, BRAND_SURFACE_CONTAINER_LOW } from '../brandDashboardTokens';
import { formatUsdCents } from '../../../utils/campaignHostSubscriptionFormat';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../../analytics/Mixpanel';

export interface BrandCampaignSubscriptionGuardProps {
    open: boolean;
    flatFeeCents: number;
    onSubscribe: () => void;
    onContinueWithFee: () => void;
    onClose: () => void;
    context?: 'launch' | 'fund';
    /** When > 0, host cannot afford the creation fee — block continue. */
    shortfallCents?: number;
    onFundWallet?: () => void;
    /** Commission (and similar) — subscribe required; hide pay-per-create. */
    subscriptionOnly?: boolean;
}

export function BrandCampaignSubscriptionGuard({
    open,
    flatFeeCents,
    onSubscribe,
    onContinueWithFee,
    onClose,
    context = 'launch',
    shortfallCents = 0,
    onFundWallet,
    subscriptionOnly = false,
}: BrandCampaignSubscriptionGuardProps) {
    const feeFormatted = formatUsdCents(flatFeeCents);
    const needsWalletTopUp = !subscriptionOnly && shortfallCents > 0;

    const title = subscriptionOnly
        ? 'Subscription required'
        : needsWalletTopUp
        ? 'Wallet top-up needed'
        : 'No Active Subscription';

    const bodyText = subscriptionOnly
        ? 'Commission campaigns are for subscribers only. You pay for results creators actually deliver — percentage commissions on sales, or a flat CPA for attributed app installs and web visits.'
        : needsWalletTopUp
        ? `Your campaign wallet is short ${formatUsdCents(
              shortfallCents,
          )} for the ${feeFormatted} creation fee. Add funds, or subscribe for unlimited campaign creation.`
        : context === 'fund'
        ? "You don't have an active campaign host plan. Subscribe to fund campaigns at no extra cost, or continue and pay a platform fee."
        : "You don't have an active campaign host plan. Subscribe to launch campaigns at no extra cost, or continue and pay a platform fee.";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 4,
                    p: { xs: 3, sm: 4 },
                },
            }}>
            <IconButton
                onClick={onClose}
                size="small"
                sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.4)' }}>
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <WorkspacePremiumIcon sx={{ fontSize: 36, color: BRAND_LUXE_PRIMARY }} />
                </Box>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 20,
                        fontWeight: 800,
                        color: '#fff',
                        mb: 1,
                    }}>
                    {title}
                </Typography>
                <Typography sx={{ color: '#adaaaa', fontSize: 14, lineHeight: 1.6, textAlign: 'left' }}>
                    {bodyText}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    border: '1px solid rgba(255,193,7,0.2)',
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                }}>
                <WarningAmberIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: '#d4cfc0', lineHeight: 1.5 }}>
                    {subscriptionOnly ? (
                        <>
                            Where a working budget applies (for example app installs or web visits), that amount is
                            locked in escrow before publish, per platform policy — so payouts are funded as results
                            clear.
                        </>
                    ) : needsWalletTopUp ? (
                        <>
                            Creation fee:{' '}
                            <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                {feeFormatted}
                            </Box>
                            . Shortfall:{' '}
                            <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                {formatUsdCents(shortfallCents)}
                            </Box>
                            .
                        </>
                    ) : (
                        <>
                            Without a subscription, a platform fee of{' '}
                            <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                {feeFormatted}
                            </Box>{' '}
                            will be charged from your campaign wallet.
                        </>
                    )}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                    fullWidth
                    onClick={() => {
                        trackSubscriptionAction(MIXPANEL_EVENT.SUBSCRIBE_CLICK, {
                            buttonName: subscriptionOnly
                                ? 'SUBSCRIBE TO UNLOCK COMMISSION'
                                : 'SUBSCRIBE FOR UNLIMITED CREATION',
                            buttonAction: 'SUBSCRIBE',
                            subscriptionType: 'campaign_host',
                            context,
                            subscriptionOnly: Boolean(subscriptionOnly),
                        });
                        onSubscribe();
                    }}
                    sx={{
                        py: 1.75,
                        borderRadius: 99,
                        background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY} 0%, #feb700 100%)`,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 800,
                        fontSize: 15,
                        color: '#392700',
                        textTransform: 'none',
                        boxShadow: '0 8px 24px rgba(255,193,7,0.25)',
                        '&:hover': { boxShadow: '0 12px 32px rgba(255,193,7,0.35)' },
                    }}>
                    {subscriptionOnly ? 'Subscribe to unlock commission' : 'Subscribe for unlimited creation'}
                </Button>
                {!subscriptionOnly && needsWalletTopUp && onFundWallet ? (
                    <Button
                        fullWidth
                        onClick={onFundWallet}
                        sx={{
                            py: 1.75,
                            borderRadius: 99,
                            border: '1px solid rgba(255,255,255,0.15)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.85)',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.25)' },
                        }}>
                        Save draft & fund wallet
                    </Button>
                ) : null}
                {!subscriptionOnly && !needsWalletTopUp ? (
                    <Button
                        fullWidth
                        onClick={onContinueWithFee}
                        sx={{
                            py: 1.75,
                            borderRadius: 99,
                            border: '1px solid rgba(255,255,255,0.15)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.65)',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.25)' },
                        }}>
                        Continue with {feeFormatted} fee
                    </Button>
                ) : null}
            </Box>
        </Dialog>
    );
}
